import React, { useState } from "react";
import { useApp } from "../store";
import FloatingSupportChat from "../components/FloatingSupportChat";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { HelpCircle, MessageSquare, FileText, Clock, CheckCircle, ArrowRight, Plus, Package, Battery, Weight, Shield, Wrench, CreditCard } from "lucide-react";
import { getFaqData } from "../data/faqData";

const iconMap = {
  Package: Package,
  Battery: Battery,
  Weight: Weight,
  Shield: Shield,
  Wrench: Wrench,
  CreditCard: CreditCard,
};

const Support = () => {
  const { t, locale } = useApp();
  const [selected, setSelected] = useState(0);
  const [expanded, setExpanded] = useState({});
  const [supportChatKey, setSupportChatKey] = useState(0);

  const { categories } = getFaqData(locale);
  const current = categories[selected];

  const toggle = (idx) => {
    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const CategoryIcon = iconMap[current.icon] || Package;

  return (
    <div className="container mx-auto py-12 px-4">
      {/* Hero */}
      <div className="mb-12 text-center">
        <div className="overline text-sky-500 mb-4">{t.supportPage.heroOverline}</div>
        <h1 className="text-4xl font-bold mb-6">{t.supportPage.heroTitle}</h1>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          {t.supportPage.heroBody}
        </p>
      </div>

      {/* FAQ Section - FIRST SECTION */}
      <section className="mb-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="mb-10 flex flex-wrap justify-center gap-3">
            {categories.map((c, i) => {
              const Icon = iconMap[c.icon] || Package;
              return (
                <button
                  key={i}
                  className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 cursor-pointer transition-colors ${
                    i === selected ? "bg-sky-500 text-white" : "bg-white text-[#0b111e]/80 hover:bg-[#f6f6f3] border border-[#e5e7eb]"
                  }`}
                  onClick={() => {
                    setSelected(i);
                    setExpanded({});
                  }}
                >
                  <Icon size={16} />
                  {c.name}
                </button>
              );
            })}
          </div>

          <section className="mb-20">
            <div className="flex items-center justify-center gap-3 mb-4">
              <CategoryIcon size={28} className="text-sky-500" />
              <h2 className="font-serif-display text-3xl md:text-4xl text-[#0b111e]">
                {current.name}
              </h2>
            </div>
            <h3 className="text-center text-[#0b111e]/60 mb-10">{current.subtitle}</h3>

            <div className="space-y-4 max-w-4xl mx-auto">
              {current.faqs.map((item, idx) => (
                <div
                  key={idx}
                  className="border border-[#e5e7eb] rounded-xl bg-white overflow-hidden transition-colors"
                >
                  <button
                    className="w-full text-left px-6 py-5 flex justify-between items-center gap-4 cursor-pointer"
                    onClick={() => toggle(idx)}
                  >
                    <span className="font-semibold text-lg text-[#0b111e]">{item.q}</span>
                    <Plus
                      size={20}
                      className={`text-sky-500 flex-shrink-0 transition-transform duration-300 ${
                        expanded[idx] ? "rotate-45" : ""
                      }`}
                    />
                  </button>
                  {expanded[idx] && (
                    <div className="px-6 pb-5 pt-0">
                      <p className="text-[#0b111e]/70 text-sm leading-relaxed border-t border-[#e5e7eb] pt-4">
                        {item.a}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      </section>

      {/* Strong CTAs near top */}
      <section className="mb-16 flex flex-wrap justify-center gap-4">
        <a href="/support/technical-request" className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
          <MessageSquare size={18} /> {t.supportPage.openTicket}
        </a>
        <Button variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-50">
          <MessageSquare size={18} className="mr-2" /> {t.supportPage.whatsappTriagem}
        </Button>
        <Button variant="outline">
          <FileText size={18} className="mr-2" /> {t.supportPage.falarSuporte}
        </Button>
      </section>

      {/* Operational categories */}
      <section className="mb-20">
        <h2 className="text-2xl font-semibold mb-8 text-center">{t.supportPage.whatNeed}</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { title: t.supportPage.diagTitle, desc: t.supportPage.diagDesc },
            { title: t.supportPage.garantiaTitle, desc: t.supportPage.garantiaDesc },
            { title: t.supportPage.pecasTitle, desc: t.supportPage.pecasDesc },
            { title: t.supportPage.manutTitle, desc: t.supportPage.manutDesc },
          ].map((card, i) => (
            <Card key={i} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="text-lg">{card.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-4">{card.desc}</p>
                <a href={i === 1 ? "/warranty" : i === 0 ? "/support#diagnostico" : "/contact"} className="text-sky-500 hover:underline text-sm inline-flex items-center gap-1">
                  {t.supportPage.access} <ArrowRight size={14} />
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Triage flow */}
      <section className="mb-20 bg-slate-50 rounded-lg p-8">
        <h2 className="text-2xl font-semibold mb-6">{t.supportPage.triagemTitle}</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { step: "1", title: t.supportPage.step1Title, desc: t.supportPage.step1Desc },
            { step: "2", title: t.supportPage.step2Title, desc: t.supportPage.step2Desc },
            { step: "3", title: t.supportPage.step3Title, desc: t.supportPage.step3Desc },
            { step: "4", title: t.supportPage.step4Title, desc: t.supportPage.step4Desc },
          ].map((item, i) => (
            <div key={i} className="flex flex-col items-center text-center">
              <div className="w-10 h-10 rounded-full bg-sky-500 text-white flex items-center justify-center font-bold mb-3">{item.step}</div>
              <h3 className="font-semibold mb-2">{item.title}</h3>
              <p className="text-slate-600 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SLA / Response expectations */}
      <section className="mb-20 border border-slate-200 rounded-lg p-8">
        <h2 className="text-2xl font-semibold mb-4 flex items-center gap-3">
          <Clock className="text-sky-500" /> {t.supportPage.slaTitle}
        </h2>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <CheckCircle className="text-emerald-500 mt-1 flex-shrink-0" size={18} />
            <span><strong>{t.supportPage.sla1.split(":")[0]}:</strong> {t.supportPage.sla1.split(":").slice(1).join(":")}</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="text-emerald-500 mt-1 flex-shrink-0" size={18} />
            <span><strong>{t.supportPage.sla2.split(":")[0]}:</strong> {t.supportPage.sla2.split(":").slice(1).join(":")}</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="text-emerald-500 mt-1 flex-shrink-0" size={18} />
            <span><strong>{t.supportPage.sla3.split(":")[0]}:</strong> {t.supportPage.sla3.split(":").slice(1).join(":")}</span>
          </li>
        </ul>
        <p className="text-sm text-slate-500 mt-4">{t.supportPage.slaNote}</p>
      </section>

      {/* Channels */}
      <section className="mb-20">
        <h2 className="text-2xl font-semibold mb-6">{t.supportPage.channelsTitle}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { channel: t.supportPage.portalChannel, detail: t.supportPage.portalDetail },
            { channel: t.supportPage.emailChannel, detail: t.supportPage.emailDetail },
            { channel: t.supportPage.whatsappChannel, detail: t.supportPage.whatsappDetail },
          ].map((item, i) => (
            <div key={i} className="border border-slate-200 rounded-lg p-6">
              <h3 className="font-semibold mb-2">{item.channel}</h3>
              <p className="text-slate-600 text-sm">{item.detail}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Internal links */}
      <section className="mb-20 text-center">
        <p className="text-slate-600 mb-6">{t.supportPage.relatedPages}</p>
        <div className="flex flex-wrap justify-center gap-4">
          <a href="/faq" className="text-sky-500 hover:underline inline-flex items-center gap-1">
            {t.supportPage.faqLink} <ArrowRight size={14} />
          </a>
          <a href="/warranty" className="text-sky-500 hover:underline inline-flex items-center gap-1">
            {t.supportPage.warrantyLink} <ArrowRight size={14} />
          </a>
          <a href="/compare" className="text-sky-500 hover:underline inline-flex items-center gap-1">
            {t.supportPage.compareLink} <ArrowRight size={14} />
          </a>
          <a href="/book-demo" className="text-sky-500 hover:underline inline-flex items-center gap-1">
            {t.supportPage.bookLink} <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* Bottom CTAs */}
      <div className="flex flex-wrap gap-4 justify-center">
        <a href="/support/technical-request" className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
          <MessageSquare size={18} /> {t.supportPage.openTicket}
        </a>
        <Button
          variant="outline"
          onClick={() => setSupportChatKey((k) => k + 1)}
        >
          <FileText size={18} className="mr-2" /> {t.supportPage.falarSuporte}
        </Button>
        <Button variant="outline">
          {t.supportPage.acompanharChamado}
        </Button>
        <Button variant="outline">
          {t.supportPage.enviarDocs}
        </Button>
      </div>
      <FloatingSupportChat initialOpen={true} />
    </div>
  );
};

export default Support;
